import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill } from "@/components/common";
import { LifeBuoy, X } from "lucide-react";
import DataList from "@/components/DataList";
import MobileSheet from "@/components/MobileSheet";

export default function TicketsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "", category: "other", priority: "normal" });
  const [err, setErr] = useState("");

  const load = () => api.get("/tickets").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/tickets", form);
      setOpen(false);
      setForm({ subject: "", message: "", category: "other", priority: "normal" });
      load();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  const pillStatus = (s) => s === "in_progress" ? "processing" : (s === "resolved" || s === "closed") ? "closed" : "open";

  const columns = [
    { key: "code", label: "Code",
      render: t => <span className="font-mono" style={{ color: "var(--amber-400)" }}>{t.code}</span>,
      search: t => t.code },
    { key: "subject", label: "Subject", render: t => t.subject, search: t => t.subject },
    { key: "category", label: "Category", render: t => <span style={{ color: "var(--ink-dim)", fontSize: 13 }}>{t.category}</span>, search: t => t.category },
    { key: "priority", label: "Priority",
      render: t => <span className="pill" style={{ borderColor: "var(--line)", color: t.priority === "urgent" ? "var(--red)" : t.priority === "high" ? "var(--amber-400)" : "var(--ink-dim)" }}>{t.priority}</span> },
    { key: "status", label: "Status", render: t => <StatusPill status={pillStatus(t.status)} /> },
    { key: "updated", label: "Updated", render: t => <span className="font-mono" style={{ color: "var(--ink-mute)", fontSize: 12 }}>{new Date(t.updated_at).toLocaleString()}</span> },
  ];

  const filters = [
    { key: "status", label: "Status",
      options: [
        { value: "all", label: "ALL" },
        { value: "open", label: "OPEN" },
        { value: "in_progress", label: "IN PROGRESS" },
        { value: "resolved", label: "RESOLVED" },
        { value: "closed", label: "CLOSED" },
      ],
      match: (row, value) => value === "all" || row.status === value },
    { key: "priority", label: "Priority",
      options: [
        { value: "all", label: "ALL" },
        { value: "urgent", label: "URGENT" },
        { value: "high", label: "HIGH" },
        { value: "normal", label: "NORMAL" },
        { value: "low", label: "LOW" },
      ],
      match: (row, value) => value === "all" || row.priority === value },
    { key: "category", label: "Category",
      options: [
        { value: "all", label: "ALL" },
        { value: "billing", label: "BILLING" },
        { value: "dispute", label: "DISPUTE" },
        { value: "verification", label: "VERIFICATION" },
        { value: "technical", label: "TECHNICAL" },
        { value: "other", label: "OTHER" },
      ],
      match: (row, value) => value === "all" || row.category === value },
  ];

  return (
    <>
      <PageHeader
        title="Support & Disputes"
        subtitle={user.role === "admin" ? "All open tickets across the platform." : "Open a ticket for billing, disputes, verification or technical help."}
        actions={user.role !== "admin" && <button onClick={() => setOpen(true)} data-testid="new-ticket" className="btn btn-primary"><LifeBuoy size={14} /> New Ticket</button>}
      />

      <MobileSheet open={open} onClose={() => setOpen(false)} title="NEW TICKET" testId="new-ticket-sheet">
        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}><label className="label">Subject *</label><input className="input" required data-testid="ticket-subject" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} /></div>
            <div><label className="label">Category</label>
              <select className="input" data-testid="ticket-category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {["billing", "dispute", "verification", "technical", "other"].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="label">Priority</label>
              <select className="input" data-testid="ticket-priority" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {["low", "normal", "high", "urgent"].map(c => <option key={c}>{c}</option>)}
              </select></div>
          </div>
          <div><label className="label">Message *</label><textarea className="input" required style={{ minHeight: 110 }} data-testid="ticket-message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} /></div>
          {err && <div style={{ color: "var(--red)" }}>{err}</div>}
          <div className="form-action-row">
            <button className="btn btn-primary" data-testid="submit-ticket">Open ticket</button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      </MobileSheet>

      <DataList
        testId="tickets-list"
        rows={rows}
        columns={columns}
        filters={filters}
        rowProps={t => ({ onClick: () => setActive(t), style: { cursor: "pointer" }, "data-testid": `ticket-row-${t.code}` })}
        emptyText="No tickets."
        searchPlaceholder="Search by code or subject…"
      />

      {active && <TicketDrawer ticket={active} onClose={() => setActive(null)} onChange={() => { load(); }} />}
    </>
  );
}

function TicketDrawer({ ticket, onClose, onChange }) {
  const { user } = useAuth();
  const [t, setT] = useState(ticket);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const reload = () => api.get(`/tickets/${t.id}`).then(r => setT(r.data));

  // Lock body scroll while the drawer is open (iOS-safe).
  useEffect(() => {
    const scrollY = window.scrollY;
    const prev = {
      position: document.body.style.position, top: document.body.style.top,
      width: document.body.style.width, overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      Object.assign(document.body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, []);

  const reply = async () => {
    if (!msg.trim()) return;
    await api.post(`/tickets/${t.id}/reply`, { message: msg, internal });
    setMsg(""); reload(); onChange();
  };
  const setStatus = async (status) => { await api.post(`/tickets/${t.id}/status`, { status }); reload(); onChange(); };
  const pillStatus = (s) => s === "in_progress" ? "processing" : (s === "resolved" || s === "closed") ? "closed" : "open";

  return (
    <div data-testid="ticket-drawer" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="ticket-drawer-panel" style={{ width: 560, maxWidth: "100%", height: "100vh", background: "var(--navy-800)", borderLeft: "1px solid var(--line)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 20, borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--amber-400)", letterSpacing: ".1em" }}>{t.code}</div>
            <div className="font-serif-display" style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{t.subject}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <StatusPill status={pillStatus(t.status)} />
              <span className="pill" style={{ borderColor: "var(--line)", color: "var(--ink-dim)" }}>{t.category}</span>
              <span className="pill" style={{ borderColor: "var(--line)", color: t.priority === "urgent" ? "var(--red)" : "var(--amber-400)" }}>{t.priority}</span>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} data-testid="ticket-drawer-close" style={{ padding: 8, minHeight: 36 }}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          {(t.messages || []).map(m => (
            <div key={m.id} style={{ background: m.role === "admin" ? "var(--navy-700)" : "var(--navy-900)", border: "1px solid var(--line)", borderLeft: m.internal ? "3px solid var(--amber-500)" : m.role === "admin" ? "3px solid #60a5fa" : "3px solid var(--line)", padding: 14, borderRadius: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ink-mute)", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <span className="font-mono" style={{ textTransform: "uppercase", letterSpacing: ".1em" }}>{m.role}{m.internal ? " · INTERNAL" : ""}</span>
                <span>{new Date(m.at).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{m.message}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: 16, borderTop: "1px solid var(--line)", display: "grid", gap: 10 }}>
          <textarea className="input" value={msg} onChange={e => setMsg(e.target.value)} placeholder="Type a reply…" style={{ minHeight: 70 }} data-testid="ticket-reply-input" />
          {user.role === "admin" && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "var(--ink-dim)", display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} /> Internal note (not visible to user)
              </label>
            </div>
          )}
          <div className="form-action-row" style={{ justifyContent: "space-between" }}>
            <button className="btn btn-primary" data-testid="ticket-reply-btn" onClick={reply}>Send reply</button>
            {user.role === "admin" && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {t.status !== "in_progress" && <button className="btn btn-ghost" onClick={() => setStatus("in_progress")}>Start work</button>}
                {t.status !== "resolved" && <button className="btn btn-ghost" onClick={() => setStatus("resolved")}>Resolve</button>}
                {t.status !== "closed" && <button className="btn btn-danger" onClick={() => setStatus("closed")}>Close</button>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
